import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import type { Facility, FacilityInsert } from "../types/facility";
import { useAuth } from "./useAuth";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";

/**
 * Fetch facilities list — role-aware.
 *
 * Platform admins/managers are confined to /api/admin/* (the portal-scoped
 * /facilities deliberately 403s them, US-065), so this hook routes them to
 * /admin/facilities automatically and applies the group-home filter
 * client-side. Everyone else hits the facility-scoped /facilities. Both
 * queries stay disabled until auth has resolved, so a platform user never
 * transiently fires the portal endpoint.
 *
 * This branching used to live only in useFacilityOptions(), which made the
 * raw hook a recurring 403 landmine (FacilitySwitcher, FormAssignmentModal,
 * RuleLibraryDetailModal, …). The safe behavior is now the only behavior;
 * useFacilityOptions() remains as an alias.
 */
export function useFacilities(
  groupHomeId?: string,
  options?: { enabled?: boolean },
) {
  const { data: context, isLoading: authLoading } = useAuth();
  const isPlatform =
    (context?.isPlatformAdmin || context?.isPlatformManager) ?? false;
  const ready = !authLoading && !!context;
  const enabled = (options?.enabled ?? true) && ready;

  const scoped = useQuery({
    queryKey: ["facilities", groupHomeId],
    queryFn: async () => {
      const params = groupHomeId ? `?group_home_id=${groupHomeId}` : "";
      const url = `/facilities${params}`;
      const { data } = await api.get(url);
      return data.data as Facility[];
    },
    enabled: enabled && !isPlatform,
  });

  const admin = useAdminFacilities({
    enabled: enabled && isPlatform,
    select: groupHomeId
      ? (rows) => rows.filter((f) => f.group_home_id === groupHomeId)
      : undefined,
  });

  return isPlatform ? admin : scoped;
}

/**
 * Fetch single facility with group home relation
 */
export interface FacilityWithDetails extends Facility {
  active_residents_count: number;
  group_homes?: {
    id: string;
    name: string;
  };
}

export function useFacility(facilityId: string) {
  return useQuery({
    queryKey: ["facilities", facilityId],
    queryFn: async () => {
      const { data } = await api.get(`/facilities/${facilityId}`);
      return data.data as FacilityWithDetails;
    },
    enabled: !!facilityId,
  });
}

/**
 * Create facility mutation
 */
export interface CreateFacilityResult {
  facility: Facility;
  /** The attestation packet that must be signed before the facility activates. */
  attestationPacketId: string;
}

export function useCreateFacility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (facility: FacilityInsert) => {
      const { data } = await api.post("/facilities", facility);
      // US-143 contract change: a facility is created INERT
      // (activation_status: "pending_attestation") and its Additional Facility
      // Attestation is issued alongside it, so the response is a pair. The
      // caller must present that packet — until it is signed the facility is
      // hidden from every picker and cannot hold a resident.
      return data.data as CreateFacilityResult;
    },
    // US-081: FacilityForm catches with handleFormErrors (US-080 DOM-6).
    meta: FORM_MUTATION_META,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facilities"] });
      queryClient.invalidateQueries({ queryKey: ["admin-facilities"] });
    },
  });
}

/**
 * Update facility mutation
 */
export function useUpdateFacility(facilityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (facility: Partial<FacilityInsert>) => {
      const { data } = await api.put(`/facilities/${facilityId}`, facility);
      // US-084 SL-3: the PUT response carries a synthetic field reporting
      // how many slot-based orders had their schedules regenerated.
      return data.data as Facility & { regenerated_orders?: number };
    },
    // US-081: FacilityForm catches with handleFormErrors (US-080 DOM-6).
    meta: FORM_MUTATION_META,
    // US-134 review fix: await every invalidation. `invalidateQueries`
    // refetches active observers and its promise resolves once they land —
    // awaiting them means `mutateAsync` doesn't resolve until dependent
    // reads (e.g. FacilityReminderPolicyCard's source-aware query) are
    // fresh, closing a stale-flash window where a caller's post-mutation
    // render briefly reads pre-mutation cached data.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["facilities"] }),
        queryClient.invalidateQueries({ queryKey: ["facilities", facilityId] }),
        queryClient.invalidateQueries({ queryKey: ["admin-facilities"] }),
        // US-134: a facility update can touch default_external_provider_reminder_policy —
        // keep FacilityReminderPolicyCard's source-aware read in sync.
        queryClient.invalidateQueries({
          queryKey: ["facility-reminder-policy", facilityId],
        }),
      ]);
    },
  });
}

/**
 * Delete facility mutation
 */
export function useDeleteFacility() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; FacilityDetailPage catch is sole caller.
    meta: { errorFallback: "Failed to delete facility" },
    mutationFn: async (facilityId: string) => {
      const { data } = await api.delete(`/facilities/${facilityId}`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facilities"] });
      queryClient.invalidateQueries({ queryKey: ["admin-facilities"] });
    },
  });
}

export function useAdminFacilities(options?: {
  enabled?: boolean;
  select?: (rows: Facility[]) => Facility[];
}) {
  return useQuery({
    queryKey: ["admin-facilities"],
    queryFn: async () => {
      const { data } = await api.get("/admin/facilities");
      return data.data as Facility[];
    },
    enabled: options?.enabled ?? true,
    select: options?.select,
  });
}

/**
 * Alias for useFacilities() — the role-aware branching this hook used to own
 * now lives in useFacilities itself, so the two are identical. Kept so
 * existing call sites and muscle memory keep working.
 */
export function useFacilityOptions() {
  return useFacilities();
}

/**
 * Fetch facilities for a specific group home (auditor-accessible endpoint).
 *
 * This route 404s unless the caller already has an `audit_access_requests`
 * row for the group home (US-140 AC27/AC28) — it exists as the fallback for
 * an auditor who already has a prior request/grant and lands on the request
 * form directly (refresh, bookmark, deep link). A first-ever request should
 * come with the facilities already in hand (F-fix-080: the org lookup
 * returns them, see `AuditGroupHome.facilities`) — pass `enabled: false` in
 * that case so this hook never fires the doomed-to-404 call.
 */
export function useGroupHomeFacilities(
  groupHomeId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["group-home-facilities", groupHomeId],
    queryFn: async () => {
      if (!groupHomeId) return [];
      const { data } = await api.get(
        `/audit/group-homes/${groupHomeId}/facilities`,
      );
      return data.data as Facility[];
    },
    enabled: !!groupHomeId && (options?.enabled ?? true),
  });
}
